import { capitalize as capitalizeFirstLetter } from 'lodash';
import type { DefineComponent } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';

import { useLogout } from '@/composables/useLogout';
import { useContentModeStore } from '@/stores/contentmode';
import { useHeadlineStore } from '@/stores/headline';
import { useProfileStore } from '@/stores/profile';
import { useRedirectStore } from '@/stores/redirect';
import { useTokenStore } from '@/stores/token';
import { buildContentModeKey } from '@/utils/contentModeKey';
import { navigationItems } from '@/router/navigation';
import { routerPaths } from '@/router/router-path';

const LOGGEDOUT_ROUTES = ['login', 'entralogin', '403', '404'];
const ALWAYS_ALLOWED_ROUTES = ['contactpersons']; // temorarily enabled as these pages might have fuzzy logic for now

const paramToString = (v: unknown): string =>
  typeof v === 'string' ? v : Array.isArray(v) ? (v[0] ?? '') : '';

/**
 * To dynamic route guard
 * @param to
 * @param from
 * @returns boolean or redirect object
 */
const dynamicRouteGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
): Promise<boolean | { path: string } | { name: string }> => {
  const contentModeStore = useContentModeStore();

  // let { subdomain, id, mode, subcomponent, subid } = to.params;
  const { chunk1: c1, chunk2: c2, chunk3: c3, chunk4: c4, chunk5: c5 } = to.params;
  const all = [c1, c2, c3, c4, c5]
    .filter((c) => c !== undefined)
    .join('/')
    .split('/');
  const chunk1: string = all[0] ?? '';
  const chunk2: string = all[1] ?? '';
  const chunk3: string = all[2] ?? '';
  const chunk4: string = all[3] ?? '';
  const chunk5: string = all[4] ?? '';

  // Define isAction FIRST (needed for route key generation)
  const isAction = (str: string) => ['new', 'neu', 'edit', 'bearbeiten', 'many'].includes(str);

  // Extract route parts for key generation (same logic as ContentModeSelect)
  let routeKeyForStore: string = 'default';
  let subComponent: string | undefined;
  let subComponentID: string | undefined;
  let mainComponent: string | undefined;
  let mainComponentID: string | undefined;
  let subComponentIdentifier: string | undefined;

  // Pre-parse to extract components for route key generation
  if (chunk5) {
    // contracts/123/subcontract/345/edit
    subComponentID = chunk4;
    subComponent = chunk3;
    mainComponentID = chunk2;
    mainComponent = chunk1;
    if (!isAction(chunk5)) {
      subComponentIdentifier = chunk5;
    }
  } else if (chunk4) {
    // contracts/123/subcontract/345 or /new
    if (!isAction(chunk4)) {
      subComponentID = chunk4;
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;
    } else {
      // contracts/123/subcontract/new
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;
    }
  } else if (chunk3) {
    // contracts/123/edit or /subcontract
    if (!isAction(chunk3)) {
      // contracts/123/subcontract
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;
    } else {
      // contracts/123/edit
      mainComponentID = chunk2;
      mainComponent = chunk1;
    }
  } else if (chunk2) {
    // contracts/new or /123
    if (!isAction(chunk2)) {
      mainComponentID = chunk2;
      mainComponent = chunk1;
    } else {
      mainComponent = chunk1;
    }
  } else if (chunk1) {
    // contracts
    mainComponent = chunk1;
  }

  // Generate route key from main + sub + subComponentIdentifier
  routeKeyForStore = buildContentModeKey({
    mainComponent,
    subComponent,
    subComponentIdentifier,
  });

  // Keep DesktopLayout in sync with the exact route-level key
  if (contentModeStore.activeContentModeKey !== routeKeyForStore) {
    contentModeStore.setActiveContentModeKey(routeKeyForStore);
  }

  const storedMode = contentModeStore.contentModeByRoute[routeKeyForStore];
  let _isSplitView = storedMode === 'split';

  // Rest of the variable declarations (already initialized above: mainComponent, mainComponentID, subComponent, subComponentID)
  let mainComponentAction: string | undefined;
  let subComponentAction: string | undefined;
  let componentNameComputed: string;
  // already initialized above for route key generation
  let mainSlotComponentNameComputed: string;
  let splitSlotComponentNameComputed: string;
  let componentPath: string;
  let mainComponentPath: string;
  let splitComponentPath: string;
  let subsplitComponentPath: string;

  // const isAction = (str: string) => ['new', 'neu', 'edit', 'bearbeiten', 'many'].includes(str);

  const folder = () => (mainComponent ? `${mainComponent.toLowerCase()}/` : '/');
  const subfolder = () => (subComponent ? `${subComponent.toLowerCase()}/` : '');
  const mainPath = () => `../views/${folder()}`;
  const subPath = () => `../views/${folder()}${subfolder()}`;
  const emptyPath = `../views/empty/Empty.vue`;

  // `contracts/123/subcontract/345/edit`
  if (!!chunk5) {
    subComponentAction = isAction(chunk5) ? chunk5 : '';
    subComponentIdentifier = !isAction(chunk5) ? chunk5 : '';
    subComponentID = chunk4;
    subComponent = chunk3;
    mainComponentID = chunk2;
    mainComponent = chunk1;

    componentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;
    mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
    splitSlotComponentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;

    componentPath = `${subPath()}${componentNameComputed}.vue`;
    mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
    splitComponentPath = `${mainPath()}${mainSlotComponentNameComputed}Detail.vue`;
    subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;

    // `contracts/123/subcontract/new`
    // `contracts/123/subcontract/345`
  } else if (!!chunk4) {
    // `contracts/123/subcontract/new`
    if (isAction(chunk4)) {
      subComponentAction = chunk4;
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${subPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
      // splitComponentPath = `${emptyPath}`;

      // `contracts/123/subcontract/345`
    } else {
      subComponentID = chunk4;
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;

      componentPath = `${subPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${mainSlotComponentNameComputed}Detail.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    }

    // `contracts/123/edit`
    // `contracts/123/subcontract`
  } else if (!!chunk3) {
    // `contracts/123/edit`
    if (isAction(chunk3)) {
      mainComponentAction = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      const nameByAction = chunk3 === 'many' ? 'Many' : 'Detail';

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}${nameByAction}`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}${nameByAction}`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;

      // `contracts/123/subcontract`
    } else {
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(subComponent)}`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    }

    // `contracts/new`
    // `contracts/123`
  } else if (!!chunk2) {
    // `contracts/new`
    if (isAction(chunk2)) {
      mainComponentAction = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    } else if (chunk2?.startsWith('batch')) {
      // `contracts/batch` or `contracts/batch-classify` or `contracts/batch-somethingelse`
      mainComponent = chunk1;

      const batchSuffix = chunk2.slice(5);
      const batchAction = batchSuffix ? capitalizeFirstLetter(batchSuffix.replace(/^-/, '')) : '';

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}Batch${batchAction}`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Batch${batchAction}`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;

      _isSplitView = false; // Batch page probably doesn't need split view
    } else {
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    }

    // `contracts`
  } else {
    mainComponent = chunk1;

    componentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
    mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
    splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

    componentPath = `${mainPath()}${componentNameComputed}.vue`;
    mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
    splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
    subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    _isSplitView = false; // no detail availableMemory, no splitview
  }

  function cleanObject<T extends Record<string, any>>(obj: T): T {
    // List of keys to remove
    const keysToRemove: string[] = ['chunk1', 'chunk2', 'chunk3', 'chunk4', 'chunk5'];

    // Loop through object keys and delete unwanted properties
    for (const key in obj) {
      if (
        obj[key] === '' ||
        obj[key] === null ||
        obj[key] === undefined ||
        keysToRemove.includes(key)
      ) {
        delete obj[key];
      }
    }
    return obj;
  }

  const idParam = subComponentID || mainComponentID || '';
  const modeParam = subComponentAction || mainComponentAction || '';
  const component = subComponent || mainComponent;

  to.name = `DynamicRoute-${componentNameComputed}${subComponentAction || mainComponentAction ? capitalizeFirstLetter((subComponentAction || mainComponentAction) + '') : ''}`;
  const rawParams = {
    ...to.params,
    mainComponentID: mainComponentID || '',
    subComponentID: subComponentID || '',
    id: idParam || '',
    mainComponentMode: mainComponentAction || '',
    subComponentMode: subComponentAction || '',
    mode: modeParam || '',
    component: component || '',
    mainComponent: mainComponent || '',
    subComponent: subComponent || '',
    subComponentIdentifier: subComponentIdentifier,
  };

  // Filter out isSplitView if it exists (route params must be strings, not booleans)
  const { isSplitView: _, ...cleanedParams } = rawParams as any;

  to.params = cleanObject({
    ...cleanedParams,
    subComponentIdentifier: cleanedParams.subComponentIdentifier || '',
  });

  type VueComponentModule = { default: DefineComponent };
  const viewsContext = import.meta.glob('../views/**/*.vue');

  try {
    const module = viewsContext[componentPath]
      ? ((await viewsContext[componentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    const mainModule = viewsContext[mainComponentPath]
      ? ((await viewsContext[mainComponentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    const splitModule = viewsContext[splitComponentPath]
      ? ((await viewsContext[splitComponentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    const subsplitModule = viewsContext[subsplitComponentPath]
      ? ((await viewsContext[subsplitComponentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    if (to.matched[0] && to.matched[0].components) {
      to.matched[0].components.default = module.default;

      to.matched[0].components = {
        default: module.default,
        main: mainModule.default, // Assignment for the 'list' slot
        split: splitModule.default, // Assignment for the 'detail' slot
        subsplit: subsplitModule.default,
      };

      if (subComponentAction && subComponentAction !== '') {
        to.meta.subComponentAction = subComponentAction;
        to.meta.hasSubcomponentAction = true;
      } else {
        to.meta.hasSubcomponentAction = false;
      }

      if (idParam) {
        to.params.id = idParam;
      }
      if (modeParam) {
        to.params.mode = modeParam;
      }

      return true;
    } else {
      console.error('[sey-router] Matched route not found or components property is undefined.');
      return { path: `${routerPaths[404]}` };
    }
  } catch (error) {
    console.error('[sey-router] Error loading component:', error);
    return { path: `${routerPaths[404]}` };
  }
};

export async function globalNavigationGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): Promise<boolean | string | { name?: string; path?: string }> {
  // 🪟 Check if this is an external window (opened via window.open())
  const windowType = to.query.windowType as string | undefined;
  if (windowType === 'external') {
    to.meta.isExternalWindow = true;
  } else {
    to.meta.isExternalWindow = false;
  }

  // 🔄 Keep active key aligned for non-dynamic routes here.
  // For dynamic routes, the dynamicRouteGuard computes a richer key and sets it there.
  const contentModeStore = useContentModeStore();
  const currentEntity = to.path.split('/').filter(Boolean)[0] || 'default';

  // Only reset on non-dynamic routes
  if (!to.meta.dynamicRoute && contentModeStore.activeContentModeKey !== currentEntity) {
    // console.log(
    //   `[Guard] 🔄 Resetting activeKey from "${contentModeStore.activeContentModeKey}" to "${currentEntity}"`,
    // );
    contentModeStore.setActiveContentModeKey(currentEntity);
  }

  const tokenStore = useTokenStore();
  const headlineStore = useHeadlineStore();
  const redirectStore = useRedirectStore();
  const { redirectUrl } = redirectStore;
  const profileStore = useProfileStore();
  const { logout } = useLogout();
  headlineStore.$reset(); // resets headlines
  tokenStore.updateTokenFromCookies();

  // forces to stay on entralogin
  if (to.name === 'entralogin') {
    return true;
  }

  if (tokenStore.accessToken) {
    const routeSegment = paramToString(to.params.chunk1);
    const matchingNavItem = navigationItems.find((navItem) => navItem.route === `/${routeSegment}`);
    const requiresSeyboldUser = matchingNavItem ? !matchingNavItem.isCustomerAllowed : false;
    const isAlwaysAllowedRoute =
      [...LOGGEDOUT_ROUTES, ...ALWAYS_ALLOWED_ROUTES].includes(to.name as string) ||
      [...LOGGEDOUT_ROUTES, ...ALWAYS_ALLOWED_ROUTES].includes(to.params?.chunk1 as string);
    const isSeboldUser = !!profileStore.userProfile?.isSeyboldUser;

    const isAllowed = isAlwaysAllowedRoute || isSeboldUser || !requiresSeyboldUser;

    if (!isAllowed) {
      // hasLogging('sey-router') &&
      console.warn('[sey-router] Zugriff verweigert: Nur für Seybold-User erlaubt.', to.path);

      return { name: '403' };
    }

    if (redirectUrl) {
      if (to.path === new URL(redirectUrl, window.location.origin).pathname) {
        // clear redirect url – when redirect url is met in reality
        redirectStore.clear();
      } else {
        // redirect if not yet happened
        redirectStore.clear();
        return redirectUrl;
      }
    }

    return to.meta.dynamicRoute ? dynamicRouteGuard(to, from) : true;
  }

  if (LOGGEDOUT_ROUTES.includes(to.name as string)) {
    // hasLogging('sey-router') &&
    return to.meta.dynamicRoute ? dynamicRouteGuard(to, from) : true;
  }

  // hasLogging('sey-router') &&
  logout({ redirect: true });
  return false; // Cancel navigation, logout will handle redirect
}
